import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, IconButton, TextField, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { integrationApiService, ShopifyTaxonomyNode, ShopifyTaxonomyOption } from '../../services/integrationApi';
import { taxonomyLabel } from './mappingModel';

interface ShopifyCategoryPickerProps {
  'aria-label': string;
  /** A taxonomy GID; empty means the empty choice. */
  value: string;
  /** Known labels (the curated list plus any looked-up GIDs), for the closed field's text. */
  options: ShopifyTaxonomyOption[];
  onChange: (gid: string) => void;
  /** Text of the empty choice ("Not mapped"). Omit for a picker that always holds a category. */
  emptyLabel?: string;
  sx?: SxProps<Theme>;
}

type Row =
  | { kind: 'clear'; key: string; label: string }
  | { kind: 'up'; key: string; label: string; target: string | null }
  | { kind: 'node'; key: string; label: string; node: ShopifyTaxonomyNode };

const PREFIX = 'gid://shopify/TaxonomyCategory/';
const SEARCH_MIN = 2;
const SEARCH_DELAY_MS = 250;

/** The GID one level up (`co-2-7-4` to `co-2-7`), or null at Level 1. */
const parentGidOf = (gid: string | null | undefined): string | null => {
  if (!gid || !gid.startsWith(PREFIX)) return null;
  const id = gid.slice(PREFIX.length);
  const dash = id.lastIndexOf('-');
  return dash < 0 ? null : `${PREFIX}${id.slice(0, dash)}`;
};

/**
 * Shopify Standard Product Category picker over the full taxonomy (served by
 * `GET /integrations/shopify/taxonomy`). Opens on the level of the current choice; a row
 * picks that category and its › opens the level under it (or press → / ←); typing searches
 * every level. Shows Shopify's full breadcrumb. The value is always the GID.
 */
const ShopifyCategoryPicker: React.FC<ShopifyCategoryPickerProps> = ({
  value,
  options,
  onChange,
  emptyLabel,
  sx,
  'aria-label': ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [parent, setParent] = useState<string | null>(null);
  // The node just picked, so its breadcrumb shows before the caller's label lookup lands.
  const [picked, setPicked] = useState<ShopifyTaxonomyNode | null>(null);
  const navigating = useRef(false);
  const highlighted = useRef<Row | null>(null);

  useEffect(() => {
    const next = input.trim();
    const timer = setTimeout(() => setQuery(next), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [input]);
  const searching = open && query.length >= SEARCH_MIN && input.trim().length >= SEARCH_MIN;

  const level = useQuery({
    queryKey: ['shopify-taxonomy', 'level', parent ?? ''],
    queryFn: () => integrationApiService.browseShopifyTaxonomy({ parent: parent ?? undefined }),
    enabled: open && !searching,
    staleTime: Infinity,
    retry: false,
  });
  const search = useQuery({
    queryKey: ['shopify-taxonomy', 'search', query],
    queryFn: () => integrationApiService.browseShopifyTaxonomy({ q: query }),
    enabled: searching,
    staleTime: Infinity,
    retry: false,
  });

  const active = searching ? search : level;

  const known = options.find((option) => option.gid === value);
  // Empty shows `emptyLabel` as the placeholder rather than as text.
  const valueText = !value
    ? ''
    : picked?.gid === value
      ? picked.fullName
      : known?.fullName ?? taxonomyLabel(options, value);

  const rows: Row[] = [];
  if (emptyLabel !== undefined && !searching) rows.push({ kind: 'clear', key: 'clear', label: emptyLabel });
  if (searching) {
    for (const node of search.data?.items || []) rows.push({ kind: 'node', key: node.gid, label: node.fullName, node });
  } else {
    const path = level.data?.path || [];
    if (parent && path.length > 0) {
      const up = path.length > 1 ? path.slice(0, -1).map((node) => node.name).join(' > ') : 'All categories';
      rows.push({ kind: 'up', key: 'up', label: `Back to ${up}`, target: parentGidOf(parent) });
    }
    for (const node of level.data?.items || []) rows.push({ kind: 'node', key: node.gid, label: node.fullName, node });
  }
  const levelName = !searching && parent ? level.data?.path?.map((node) => node.name).join(' > ') : undefined;

  /** Show the level under `target` (null: Level 1). `fromSelect`: picked as a row, so MUI is about to close the list. */
  const drill = (target: string | null, fromSelect = false) => {
    navigating.current = fromSelect;
    setParent(target);
    setInput('');
    setQuery('');
  };

  const close = () => {
    setOpen(false);
    setInput('');
    setQuery('');
  };

  return (
    <Autocomplete<Row, false, false, false>
      size="small"
      open={open}
      onOpen={() => {
        setParent(parentGidOf(value));
        setOpen(true);
      }}
      onClose={() => {
        // Opening a level keeps the list open; anything else closes it.
        if (navigating.current) {
          navigating.current = false;
          return;
        }
        close();
      }}
      // Above a Dialog, so the list opens over the modal it sits in.
      slotProps={{ popper: { sx: { zIndex: (theme) => theme.zIndex.modal + 1 } } }}
      options={rows}
      // Never holds a row: every pick goes through onChange, so picking the same row twice still fires.
      value={null}
      inputValue={open ? input : valueText}
      onInputChange={(_, next, reason) => {
        if (reason === 'input') setInput(next);
      }}
      onChange={(_, row) => {
        if (!row) return;
        if (row.kind === 'up') {
          drill(row.target, true);
          return;
        }
        if (row.kind === 'clear') onChange('');
        else {
          setPicked(row.node);
          onChange(row.node.gid);
        }
      }}
      onHighlightChange={(_, row) => {
        highlighted.current = row;
      }}
      onKeyDown={(event) => {
        if (input) return;
        const row = highlighted.current;
        if (event.key === 'ArrowRight' && row?.kind === 'node' && row.node.hasChildren) {
          event.preventDefault();
          (event as unknown as { defaultMuiPrevented: boolean }).defaultMuiPrevented = true;
          drill(row.node.gid);
        } else if (event.key === 'ArrowLeft' && parent) {
          event.preventDefault();
          (event as unknown as { defaultMuiPrevented: boolean }).defaultMuiPrevented = true;
          drill(parentGidOf(parent));
        }
      }}
      filterOptions={(list) => list}
      getOptionLabel={(row) => row.label}
      getOptionKey={(row) => row.key}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      // Only the query on screen (the search while typing, else the level) decides; its error wins over loading and empty.
      loading={active.isFetching && !active.isError}
      noOptionsText={active.isError ? 'Could not load Shopify categories' : searching ? 'No Shopify category matches' : 'No categories'}
      renderOption={(props, row) => {
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
        if (row.kind !== 'node') {
          return (
            <Box component="li" key={key} {...rest} sx={{ gap: 0.5 }}>
              {row.kind === 'up' && <ChevronLeft fontSize="small" sx={{ color: 'text.secondary' }} />}
              <Typography variant="body2" color={row.kind === 'up' ? 'text.secondary' : undefined}>{row.label}</Typography>
            </Box>
          );
        }
        return (
          <Box component="li" key={key} {...rest} sx={{ gap: 1, justifyContent: 'space-between !important' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2">{row.node.name}</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ overflowWrap: 'anywhere' }}>
                {row.node.fullName}
              </Typography>
            </Box>
            {row.node.hasChildren && (
              <IconButton
                size="small"
                aria-label={`Open ${row.node.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  drill(row.node.gid);
                }}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      }}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={open ? (levelName ? `Search, or pick under ${levelName}` : 'Search Shopify categories') : emptyLabel}
          slotProps={{ htmlInput: { ...params.inputProps, 'aria-label': ariaLabel, title: open ? undefined : valueText } }}
        />
      )}
    />
  );
};

export default ShopifyCategoryPicker;
